import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { toast } from 'sonner';
import { Download, Camera, ShoppingBag, Image, ArrowLeft, Star } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function GaleriePage() {
  const { orderId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND}/api/galerie/${orderId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setGallery(d))
      .catch((err) => { /* eslint-disable-line no-empty */ })
      .finally(() => setLoading(false));
  }, [orderId]);

  async function orderPrints() {
    if (selectedPhotos.length === 0) { toast.error('Sélectionnez au moins une photo'); return; }
    setOrdering(true);
    try {
      const res = await fetch(`${BACKEND}/api/galerie/${orderId}/commander-tirages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photos: selectedPhotos, format: '10x15', quantite: 1 })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Commande de tirages enregistrée !', { description: 'Nous vous contacterons pour la livraison.' });
        setSelectedPhotos([]);
      }
    } catch (e) { toast.error('Erreur'); } finally { setOrdering(false); }
  }

  if (loading) return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}><Header />
      <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
        <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} />
      </div>
    </div>
  );

  if (!gallery) return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}><Header />
      <div className="container-ose text-center" style={{ paddingTop: '6rem' }}>
        <Image size={48} className="mx-auto mb-4" style={{ color: 'rgba(255,255,255,0.15)' }} />
        <p style={{ color: 'rgba(255,255,255,0.4)' }}>Galerie non trouvée</p>
      </div>
      <Footer />
    </div>
  );

  const photos = gallery.photos || [];

  return (
    <div style={{ background: '#0D0D0D', minHeight: '100vh' }}>
      <Header />
      <div className="container-ose section-py">
        <Link to="/" className="flex items-center gap-2 text-sm mb-6" style={{ color: 'rgba(255,255,255,0.4)' }}>
          <ArrowLeft size={16} /> Retour
        </Link>

        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-2xl font-bold" style={{ color: '#FFFFFF' }}>Galerie de votre événement</h1>
            {gallery.event_date && (
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {formatDate(gallery.event_date)}
              </p>
            )}
          </div>
          {gallery.tirages_disponibles && selectedPhotos.length > 0 && (
            <button
              onClick={orderPrints}
              disabled={ordering}
              className="btn-gold px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2"
            >
              <ShoppingBag size={16} /> Commander {selectedPhotos.length} tirage{selectedPhotos.length > 1 ? 's' : ''}
            </button>
          )}
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-20">
            <Camera size={48} className="mx-auto mb-4" style={{ color: 'rgba(255,255,255,0.1)' }} />
            <p className="font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.4)' }}>Galerie en cours de préparation</p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.25)' }}>Vos photos seront disponibles prochainement.</p>
          </div>
        ) : (
          <div>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.4)' }}>{photos.length} photo{photos.length > 1 ? 's' : ''} — Sélectionnez pour commander des tirages</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((photo, i) => {
                const isSelected = selectedPhotos.includes(photo.id);
                return (
                  <div
                    key={photo.id || i}
                    className="relative cursor-pointer rounded-xl overflow-hidden group"
                    onClick={() => {
                      if (isSelected) setSelectedPhotos(prev => prev.filter(p => p !== photo.id));
                      else setSelectedPhotos(prev => [...prev, photo.id]);
                    }}
                    style={{ border: isSelected ? '2px solid #D4AF37' : '2px solid transparent' }}
                  >
                    <img
                      src={photo.url || photo.base64}
                      alt={photo.caption || `Photo ${i + 1}`}
                      className="w-full aspect-square object-cover"
                    loading="lazy" />
                    {isSelected && (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.3)' }}>
                        <Star size={24} fill="#D4AF37" color="#D4AF37" />
                      </div>
                    )}
                    {gallery.download_enabled && (
                      <a
                        href={photo.url || photo.base64}
                        download={`show-evenement-${i + 1}.jpg`}
                        onClick={e => e.stopPropagation()}
                        className="absolute bottom-2 right-2 w-8 h-8 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: 'rgba(13,13,13,0.8)', color: '#D4AF37' }}
                      >
                        <Download size={14} />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>

            {gallery.tirages_disponibles && (
              <div className="mt-8 p-5 rounded-xl" style={{ background: '#161616', border: '1px solid #2A2A2A' }}>
                <h3 className="font-semibold text-sm mb-2" style={{ color: '#D4AF37' }}>Commander des tirages supplémentaires</h3>
                <p className="text-xs mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Format 10x15cm, livraison à domicile. Sélectionnez vos photos favorites ci-dessus.</p>
                <button
                  onClick={orderPrints}
                  disabled={ordering || selectedPhotos.length === 0}
                  className="btn-gold px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
                >
                  Commander {selectedPhotos.length || ''} tirage{selectedPhotos.length > 1 ? 's' : ''}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
